import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
    getAllEmployeeExits,
    getEmployeeExitByID,
    createEmployeeExit,
    updateEmployeeExit,
    approveEmployeeExit,
    conductExitInterview,
    updateFinalSettlement,
    deactivateEmployeeAccount,
    completeEmployeeExit,
    addExitChecklistItem,
    updateExitChecklistItem,
    deleteExitChecklistItem
} from './thunk';

export interface ExitChecklistItem {
    id: string;
    exit_id: string;
    checklist_item: string;
    checklist_category: 'IT' | 'HR' | 'Finance' | 'Admin' | 'Manager' | 'Other';
    description?: string;
    status: 'Pending' | 'In Progress' | 'Completed' | 'Not Applicable';
    assigned_to?: string;
    completed_by?: string;
    completed_at?: string;
    notes?: string;
    priority: 'Low' | 'Medium' | 'High' | 'Critical';
    created_at: string;
    updated_at: string;
}

export interface EmployeeExit {
    id: string;
    employee_id: string;
    exit_type: 'Resignation' | 'Termination' | 'Retirement' | 'End of Contract' | 'Other';
    exit_reason?: string;
    resignation_date?: string;
    last_working_day: string;
    notice_period_days?: number;
    exit_status: 'Initiated' | 'In Progress' | 'Pending Approval' | 'Approved' | 'Completed' | 'Cancelled';
    initiated_by?: string;
    approved_by?: string;
    approval_date?: string;
    approval_notes?: string;
    exit_interview_conducted: boolean;
    exit_interview_date?: string;
    exit_interview_conducted_by?: string;
    exit_interview_notes?: string;
    exit_interview_feedback?: string;
    final_settlement_amount?: number;
    final_settlement_date?: string;
    final_settlement_status: 'Pending' | 'Calculated' | 'Approved' | 'Paid';
    settlement_notes?: string;
    account_deactivated: boolean;
    account_deactivated_date?: string;
    account_deactivated_by?: string;
    handover_notes?: string;
    additional_notes?: string;
    created_at: string;
    updated_at: string;
    completed_at?: string;
    employee_name?: string;
    employee_code?: string;
    employee_email?: string;
    checklist_items?: ExitChecklistItem[];
    checklist_progress?: {
        total: number;
        completed: number;
        percentage: number;
    };
}

export interface ExitManagementState {
    exits: EmployeeExit[];
    currentExit: EmployeeExit | null;
    loading: boolean;
    error: string | null;
}

export const initialState: ExitManagementState = {
    exits: [],
    currentExit: null,
    loading: false,
    error: null
};

const exitManagementSlice = createSlice({
    name: 'exitManagement',
    initialState,
    reducers: {
        clearCurrentExit: (state) => {
            state.currentExit = null;
        },
        clearExits: (state) => {
            state.exits = [];
            state.currentExit = null;
        }
    },
    extraReducers: (builder) => {
        // Get all exits
        builder.addCase(getAllEmployeeExits.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(getAllEmployeeExits.fulfilled, (state, action: PayloadAction<EmployeeExit[]>) => {
            state.exits = action.payload || [];
            state.loading = false;
            state.error = null;
        });
        builder.addCase(getAllEmployeeExits.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string || 'Failed to fetch exit records';
        });

        // Get exit by ID
        builder.addCase(getEmployeeExitByID.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(getEmployeeExitByID.fulfilled, (state, action: PayloadAction<EmployeeExit>) => {
            state.currentExit = action.payload;
            state.loading = false;
            state.error = null;
        });
        builder.addCase(getEmployeeExitByID.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string || 'Failed to fetch exit record';
        });

        // Create exit
        builder.addCase(createEmployeeExit.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(createEmployeeExit.fulfilled, (state, action: PayloadAction<EmployeeExit>) => {
            state.exits.unshift(action.payload);
            state.currentExit = action.payload;
            state.loading = false;
            state.error = null;
        });
        builder.addCase(createEmployeeExit.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string || 'Failed to create exit record';
        });

        // Update exit
        builder.addCase(updateEmployeeExit.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(updateEmployeeExit.fulfilled, (state, action: PayloadAction<EmployeeExit>) => {
            const index = state.exits.findIndex(exit => exit.id === action.payload.id);
            if (index !== -1) {
                state.exits[index] = action.payload;
            }
            if (state.currentExit?.id === action.payload.id) {
                state.currentExit = action.payload;
            }
            state.loading = false;
            state.error = null;
        });
        builder.addCase(updateEmployeeExit.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string || 'Failed to update exit record';
        });

        // Approve exit
        builder.addCase(approveEmployeeExit.fulfilled, (state, action: PayloadAction<EmployeeExit>) => {
            const index = state.exits.findIndex(exit => exit.id === action.payload.id);
            if (index !== -1) {
                state.exits[index] = action.payload;
            }
            if (state.currentExit?.id === action.payload.id) {
                state.currentExit = action.payload;
            }
        });

        // Conduct exit interview
        builder.addCase(conductExitInterview.fulfilled, (state, action: PayloadAction<EmployeeExit>) => {
            const index = state.exits.findIndex(exit => exit.id === action.payload.id);
            if (index !== -1) {
                state.exits[index] = action.payload;
            }
            if (state.currentExit?.id === action.payload.id) {
                state.currentExit = action.payload;
            }
        });

        // Update final settlement
        builder.addCase(updateFinalSettlement.fulfilled, (state, action: PayloadAction<EmployeeExit>) => {
            const index = state.exits.findIndex(exit => exit.id === action.payload.id);
            if (index !== -1) {
                state.exits[index] = action.payload;
            }
            if (state.currentExit?.id === action.payload.id) {
                state.currentExit = action.payload;
            }
        });

        // Deactivate account
        builder.addCase(deactivateEmployeeAccount.fulfilled, (state, action: PayloadAction<EmployeeExit>) => {
            const index = state.exits.findIndex(exit => exit.id === action.payload.id);
            if (index !== -1) {
                state.exits[index] = action.payload;
            }
            if (state.currentExit?.id === action.payload.id) {
                state.currentExit = action.payload;
            }
        });

        // Complete exit
        builder.addCase(completeEmployeeExit.fulfilled, (state, action: PayloadAction<EmployeeExit>) => {
            const index = state.exits.findIndex(exit => exit.id === action.payload.id);
            if (index !== -1) {
                state.exits[index] = action.payload;
            }
            if (state.currentExit?.id === action.payload.id) {
                state.currentExit = action.payload;
            }
        });

        // Add checklist item
        builder.addCase(addExitChecklistItem.fulfilled, (state, action: PayloadAction<ExitChecklistItem>) => {
            if (state.currentExit) {
                if (!state.currentExit.checklist_items) {
                    state.currentExit.checklist_items = [];
                }
                state.currentExit.checklist_items.push(action.payload);
            }
        });

        // Update checklist item
        builder.addCase(updateExitChecklistItem.fulfilled, (state, action: PayloadAction<ExitChecklistItem>) => {
            if (state.currentExit?.checklist_items) {
                const index = state.currentExit.checklist_items.findIndex(item => item.id === action.payload.id);
                if (index !== -1) {
                    state.currentExit.checklist_items[index] = action.payload;
                }
            }
        });

        // Delete checklist item
        builder.addCase(deleteExitChecklistItem.fulfilled, (state, action: PayloadAction<string>) => {
            if (state.currentExit?.checklist_items) {
                state.currentExit.checklist_items = state.currentExit.checklist_items.filter(
                    item => item.id !== action.payload
                );
            }
        });
    }
});

export const { clearCurrentExit, clearExits } = exitManagementSlice.actions;
export default exitManagementSlice.reducer;

